import {
  API_PATHS,
  API_V1_PREFIX,
  type Job,
  type JobStatusHistoryEntry,
  type Proof,
  sortJobsForDriver,
} from '@uroute/shared';
import { useCallback, useMemo } from 'react';
import { useToast } from '@uroute/ui';
import { haptic } from './haptics';
import { commitmentTime, endOfLocalDay, isSameLocalDay } from './format';
import { isDelivered } from './jobStatus';
import { invalidate, useApiQuery } from './useQuery';
import { submitItems, type QueueItem } from './queue';

/** me/jobs may answer with a bare array or a paginated envelope. */
export function normalizeJobs(raw: unknown): Job[] {
  if (Array.isArray(raw)) return raw as Job[];
  if (raw && typeof raw === 'object') {
    const items = (raw as { items?: unknown }).items;
    if (Array.isArray(items)) return items as Job[];
  }
  return [];
}

function normalizeList<T>(raw: unknown): T[] {
  if (Array.isArray(raw)) return raw as T[];
  if (raw && typeof raw === 'object') {
    const items = (raw as { items?: unknown }).items;
    if (Array.isArray(items)) return items as T[];
  }
  return [];
}

export const MY_JOBS_PATH = `${API_V1_PREFIX}/me/jobs?includeCompleted=true&pageSize=100`;

export interface MyJobsView {
  jobs: Job[];
  /** Every non-completed job (driver-visible statuses only, server-filtered). */
  active: Job[];
  /** Due today, overdue, undated or in exception. */
  today: Job[];
  /** Due after today. */
  upcoming: Job[];
  completedToday: Job[];
  completedEarlier: Job[];
  loading: boolean;
  refreshing: boolean;
  error: string | null;
  offline: boolean;
  updatedAt: number | null;
  refresh: () => Promise<void>;
}

export function useMyJobs(): MyJobsView {
  const query = useApiQuery<unknown>('me/jobs', MY_JOBS_PATH);

  const jobs = useMemo(() => sortJobsForDriver(normalizeJobs(query.data)), [query.data]);

  const view = useMemo(() => {
    const now = new Date();
    const endToday = endOfLocalDay(now);
    const active: Job[] = [];
    const today: Job[] = [];
    const upcoming: Job[] = [];
    const completedToday: Job[] = [];
    const completedEarlier: Job[] = [];

    for (const job of jobs) {
      if (isDelivered(job.status)) {
        const when = job.completedAt ?? job.deliveredAt ?? job.updatedAt;
        (isSameLocalDay(when, now) ? completedToday : completedEarlier).push(job);
        continue;
      }
      active.push(job);
      const at = commitmentTime(job);
      if (job.status === 'exception' || at === null || at <= endToday) today.push(job);
      else upcoming.push(job);
    }
    return { active, today, upcoming, completedToday, completedEarlier };
  }, [jobs]);

  return {
    jobs,
    ...view,
    loading: query.loading,
    refreshing: query.refreshing,
    error: query.error,
    offline: query.offline,
    updatedAt: query.updatedAt,
    refresh: query.refresh,
  };
}

export function useJob(id: string | undefined) {
  return useApiQuery<unknown>(`job/${id ?? 'none'}`, `${API_PATHS.jobs}/${id ?? ''}`, {
    enabled: Boolean(id),
  });
}

export function jobFrom(raw: unknown): Job | null {
  return raw && typeof raw === 'object' && 'status' in raw ? (raw as Job) : null;
}

export function useJobHistory(id: string | undefined) {
  const query = useApiQuery<unknown>(`job/${id ?? 'none'}/history`, `${API_PATHS.jobs}/${id ?? ''}/status/history`, {
    enabled: Boolean(id),
  });
  const entries = useMemo(() => normalizeList<JobStatusHistoryEntry>(query.data), [query.data]);
  return { entries, ...query };
}

export function useJobProofs(id: string | undefined) {
  const query = useApiQuery<unknown>(`job/${id ?? 'none'}/proofs`, `${API_PATHS.proofs}?jobId=${id ?? ''}`, {
    enabled: Boolean(id),
  });
  const proofs = useMemo(() => normalizeList<Proof>(query.data), [query.data]);
  return { proofs, ...query };
}

/* ------------------------------ driver actions ---------------------------- */

export interface ActionLabels {
  success: string;
  queued: string;
}

/**
 * Runs a chain of queue items with consistent UX:
 * online ??? pushed immediately (toast on success/conflict),
 * offline ??? persisted (toast: queued for sync).
 */
export function useDriverActions() {
  const toast = useToast();

  return useCallback(
    async (items: QueueItem[], labels: ActionLabels): Promise<boolean> => {
      if (items.length === 0) return false;
      try {
        const result = await submitItems(items);
        if (result.status === 'done') {
          toast.show({ title: labels.success, tone: 'success' });
          haptic('success');
          invalidate('me/jobs');
          invalidate('job/');
          invalidate('notifications');
          return true;
        }
        if (result.status === 'queued') {
          toast.show({
            title: 'Saved on this device',
            description: `${labels.queued} — it will sync when you're back online.`,
            tone: 'warning',
            duration: 6000,
          });
          haptic('light');
          invalidate('me/jobs');
          return true;
        }
        toast.show({
          title: "Couldn't update the job",
          description: result.error,
          tone: 'danger',
          duration: 8000,
        });
        haptic('error');
        return false;
      } catch (err) {
        toast.show({
          title: "Couldn't update the job",
          description: err instanceof Error ? err.message : 'Please try again.',
          tone: 'danger',
          duration: 8000,
        });
        haptic('error');
        return false;
      }
    },
    [toast],
  );
}
